import { useMemo } from 'react'
import { Box } from '@mui/material'
import { Graph } from 'Common/Graph/Graph'
import { AdherenceReportData } from './AdherenceReport.types'
import {
  aggregateAdherenceByWeek,
  buildWeeklySkeleton,
} from './AdherenceChart.utils'

type AdherenceChartProps = {
  data: AdherenceReportData[]
  startDate?: number
  endDate?: number
}

export const AdherenceChart = ({
  data,
  startDate,
  endDate,
}: AdherenceChartProps) => {
  const weekSkeleton = useMemo(
    () =>
      startDate !== undefined && endDate !== undefined
        ? buildWeeklySkeleton(startDate, endDate)
        : undefined,
    [startDate, endDate]
  )

  const weeklyData = useMemo(
    () => aggregateAdherenceByWeek(data, weekSkeleton),
    [data, weekSkeleton]
  )

  const labels = useMemo(
    () => weeklyData.map((week) => week.label),
    [weeklyData]
  )

  const stockLineData = useMemo(
    () => [
      {
        x: labels,
        y: weeklyData.map((week) => week.unconstrainedStockTarget),
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Unconstrained Stock Target',
        line: { color: '#F59E0B' },
      },
      {
        x: labels,
        y: weeklyData.map((week) => week.constrainedStockTarget),
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Constrained Stock Target',
        line: { color: '#1D4ED8' },
      },
      {
        x: labels,
        y: weeklyData.map((week) => week.weekStartInventory),
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Actual Stock',
        line: { color: '#198754' },
      },
    ],
    [labels, weeklyData]
  )

  const adherenceBarData = useMemo(
    () => [
      {
        x: labels,
        y: weeklyData.map((week) => week.adherenceUnconstrainedPct),
        type: 'bar',
        name: 'Adherence to Unconstrained Target',
        marker: { color: '#93C5FD' },
      },
      {
        x: labels,
        y: weeklyData.map((week) => week.adherenceConstrainedPct),
        type: 'bar',
        name: 'Adherence to Constrained Target',
        marker: { color: '#1E3A8A' },
      },
    ],
    [labels, weeklyData]
  )

  return (
    <Box display="flex" flexDirection="column" gap="24px">
      <Box sx={{ height: 450 }}>
        <Graph
          data={stockLineData}
          layout={{
            title: { text: 'Stock Target vs Actual Stock' },
            xaxis: {
              title: { text: 'Week' },
              type: 'category',
              tickmode: 'array',
              tickvals: labels,
              ticktext: labels,
              tickangle: -45,
              automargin: true,
            },
            yaxis: { title: { text: 'Stock Volume' } },
            margin: { b: 120 },
          }}
        />
      </Box>
      <Box sx={{ height: 450 }}>
        <Graph
          data={adherenceBarData}
          layout={{
            title: { text: 'Adherence to Target by Week' },
            xaxis: {
              title: { text: 'Week' },
              type: 'category',
              tickmode: 'array',
              tickvals: labels,
              ticktext: labels,
              tickangle: -45,
              automargin: true,
            },
            yaxis: { title: { text: 'Adherence %' } },
            barmode: 'group',
            margin: { b: 120 },
          }}
        />
      </Box>
    </Box>
  )
}
